import { Component, ElementRef, OnInit, OnDestroy, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef, NgZone } from "@angular/core";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { BaseComponent, UserService, EventBusService } from "@mypxplat/xplat/core";

/**
 * An action with an icon. The label is still what comes back on `actionChosen`, so callers
 * compare against strings exactly as they do for plain-string actions. The icon is a
 * FontAwesome name registered in UiModule (`"trash-alt"`, or `["far", "bookmark"]`).
 */
export interface ActionItem {
  label: string;
  icon?: IconProp;
}

/** A normalised item; `divider` marks the first of the trailing "(Admin)" group. */
export interface ActionMenuItem extends ActionItem {
  divider?: boolean;
}

/**
 * "(Admin)" actions go last, in their original order, behind a divider — so a moderator's
 * menu leads with what everyone has. No divider when the menu is all-admin or has none.
 */
export function groupAdminActions(items: ActionItem[]): ActionMenuItem[] {
  const isAdmin = (i: ActionItem) => i.label.includes("(Admin)");
  const everyone: ActionMenuItem[] = items.filter((i) => !isAdmin(i));
  const admin: ActionMenuItem[] = items.filter(isAdmin);
  if (!everyone.length || !admin.length) return items;
  return [...everyone, { ...admin[0], divider: true }, ...admin.slice(1)];
}

export interface ActionArgs {
  title: string;
  /** Plain strings and `ActionItem`s mix freely; an item without an icon just has none. */
  actions: Array<string | ActionItem>;
  /**
   * The control that opened the chooser (e.g. a •••). With it, the chooser opens as a small menu
   * anchored to that control, styled like the reaction picker, instead of the bottom sheet.
   * Leave it out for the sheet. Callers don't change otherwise: the choice still comes back
   * on `actionChosen`, and dismissing still sends `false`.
   */
  anchor?: HTMLElement | EventTarget | null;
}

/** Gap between the anchor and the menu, and the menu's minimum distance from the window edge. */
const MENU_GAP = 6;
const MENU_MARGIN = 8;
/**
 * A chooser opened this soon after a choice in an anchored menu, with no anchor of its own, is
 * chained from it (••• → Share Post → Copy Link, ••• → Flag Post → reason, which waits 1s), so it
 * opens at the same anchor rather than dropping to the sheet.
 */
const CHAIN_WINDOW_MS = 1500;

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-action",
  templateUrl: "action.component.html",
  standalone: false,
})
export class ActionComponent extends BaseComponent implements OnInit, OnDestroy {
  public title: string;
  /** Normalised from `ActionArgs.actions`. */
  public actions: Array<ActionMenuItem>;
  /** Some item has an icon: the menu keeps an icon column so every label lines up. */
  public hasIcons = false;
  public show: boolean;
  /** Anchored menu (vs. the bottom sheet), and where it sits. Set before `show`. */
  public anchored = false;
  public menuTop: number = null;
  public menuLeft: number = null;
  public menuMaxHeight: number = null;
  /** The menu grows up from the anchor when there isn't room below it. */
  public menuAbove = false;
  @ViewChild("modal") modal: ElementRef;
  @ViewChild("menu") menu: ElementRef<HTMLElement>;

  public lastFocusedElement: any;

  private _anchor: HTMLElement = null;
  /** For chaining (see CHAIN_WINDOW_MS) and for the ••• toggling its own menu closed. */
  private _lastAnchor: HTMLElement = null;
  private _lastChosenAt = 0;
  private _dismissedAnchor: HTMLElement = null;
  private _dismissedAt = 0;

  constructor(
    userService: UserService,
    private _eventBus: EventBusService,
    private _cdr: ChangeDetectorRef,
    private _zone: NgZone
  ) {
    super(userService);
  }

  ngOnInit() {
    this._eventBus.observe(this._eventBus.types.showActionChooser).subscribe((result: ActionArgs) => {
      const now = Date.now();
      let anchor = result.anchor instanceof HTMLElement ? result.anchor : null;
      if (!anchor && this._lastAnchor?.isConnected && now - this._lastChosenAt < CHAIN_WINDOW_MS) anchor = this._lastAnchor;

      // The ••• was pressed again while its menu was open: the pointerdown already closed it, so
      // this click is a toggle, not a reopen. The caller is waiting on `actionChosen`; answer it.
      if (anchor && anchor === this._dismissedAnchor && now - this._dismissedAt < 400) {
        this._dismissedAnchor = null;
        setTimeout(() => this._eventBus.emit(this._eventBus.types.actionChosen, false));
        return;
      }

      // Chained from a previous choice (it's still closing, so `actions` is set): focus is on
      // that chooser's button, which is about to vanish — keep returning to what opened the first.
      if (!this.actions) this.lastFocusedElement = anchor || document.activeElement;
      this._detachMenuListeners();
      this.actions = groupAdminActions((result.actions || []).map((a) => (typeof a === "string" ? { label: a } : a)));
      this.hasIcons = this.actions.some((a) => !!a.icon);
      this.title = result.title;
      this._anchor = anchor;
      this.anchored = !!anchor;

      if (this.anchored) {
        this.show = false;
        // Rendered hidden, measured and placed, then shown in place — no jump. A timeout rather
        // than a frame so the fade-in transition runs (and frames don't fire in a hidden tab).
        this._cdr.detectChanges();
        this._positionMenu();
        this._cdr.detectChanges();
        setTimeout(() => {
          if (!this.actions || this._anchor !== anchor) return;
          this.show = true;
          this._cdr.detectChanges();
          this._attachMenuListeners();
          this._focusItem(0);
        });
      } else {
        setTimeout(() => {
          this.show = true;
          this.disableFocusOutsideModal();
        }, 100);
      }
    });
  }

  ngOnDestroy() {
    this._detachMenuListeners();
    super.ngOnDestroy();
  }

  disableFocusOutsideModal() {
    const focusableElements = document.querySelectorAll(`
      a[href], button, textarea:not([disabled]),
      input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"]),
      span[contenteditable], iframe, video
    `);

    focusableElements.forEach((element: any) => {
      if (!this.modal?.nativeElement.contains(element)) {
        element.setAttribute("tabindex", "-1");
        // Specifically for video elements, disable controls
        if (element.tagName.toLowerCase() === "video") {
          element.setAttribute("disabled", "true");
          element.controls = false;
        }
      }
    });
  }

  enableFocusOutsideModal() {
    const focusableElements = document.querySelectorAll('[tabindex="-1"], video[disabled]');

    focusableElements.forEach((element: any) => {
      element.removeAttribute("tabindex");
      // Specifically for video elements, enable controls back if needed
      if (element.tagName.toLowerCase() === "video") {
        element.removeAttribute("disabled");
        element.controls = true; // Only do this if the controls were originally intended to be shown
      }
    });
  }

  choose(action, e?) {
    if (e) e.stopPropagation();
    const wasAnchored = this.anchored;
    if (wasAnchored) {
      this._lastAnchor = this._anchor;
      this._lastChosenAt = action ? Date.now() : 0;
      this._detachMenuListeners();
    }
    this._eventBus.emit(this._eventBus.types.actionChosen, action);
    this.show = false;
    setTimeout(
      () => {
        // A choice can open ANOTHER chooser straight away (••• → Share Post → Copy Link…). That
        // one is showing by now, so leave its actions, its focus trap and its focus alone.
        if (this.show) return;
        if (!wasAnchored) this.enableFocusOutsideModal();
        // reset the focus back to the element that was focused before the modal was opened.
        if (this.lastFocusedElement?.isConnected) this.lastFocusedElement.focus();
        this.actions = undefined;
        this.anchored = false;
        this._anchor = null;
      },
      wasAnchored ? 120 : 250
    );
  }

  /** Arrow keys walk the menu, Home/End jump, Escape and Tab close it. */
  onMenuKeydown(e: KeyboardEvent) {
    const items = this._menuItems();
    const current = items.indexOf(document.activeElement as HTMLElement);
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const step = e.key === "ArrowDown" ? 1 : -1;
      this._focusItem(current < 0 ? 0 : (current + step + items.length) % items.length);
    } else if (e.key === "Home" || e.key === "End") {
      e.preventDefault();
      this._focusItem(e.key === "Home" ? 0 : items.length - 1);
    } else if (e.key === "Escape") {
      e.preventDefault();
      this.choose(false);
    } else if (e.key === "Tab") {
      // A menu, not a dialog: Tab leaves it, as it would a native one.
      this.lastFocusedElement = null;
      this.choose(false);
    }
  }

  // ---- Anchored menu ----

  private _menuItems(): HTMLElement[] {
    return Array.from(this.menu?.nativeElement.querySelectorAll<HTMLElement>(".action-menu__item") || []);
  }

  private _focusItem(index: number) {
    this._menuItems()[index]?.focus({ preventScroll: true });
  }

  /**
   * Below the anchor, start-aligned with it; above when there's more room there; nudged in
   * from either window edge; scrolling inside when neither side fits the whole list.
   */
  private _positionMenu() {
    const el = this.menu?.nativeElement;
    if (!el || !this._anchor) return;
    const a = this._anchor.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const vh = window.innerHeight;
    el.style.maxHeight = "";
    const width = el.offsetWidth;
    const height = el.offsetHeight;

    const below = vh - a.bottom - MENU_GAP - MENU_MARGIN;
    const above = a.top - MENU_GAP - MENU_MARGIN;
    this.menuAbove = height > below && above > below;
    const room = this.menuAbove ? above : below;
    this.menuMaxHeight = height > room ? Math.max(room, 120) : null;
    const shown = Math.min(height, this.menuMaxHeight ?? height);
    // Clamped: in a window too short for even the 120px floor, it stays on screen and scrolls.
    this.menuTop = Math.max(MENU_MARGIN, this.menuAbove ? a.top - MENU_GAP - shown : a.bottom + MENU_GAP);

    const maxLeft = vw - width - MENU_MARGIN;
    this.menuLeft = Math.max(MENU_MARGIN, Math.min(a.left, maxLeft));
  }

  private _onDocPointerDown = (e: PointerEvent) => {
    if (this.menu?.nativeElement.contains(e.target as Node)) return;
    if (this._anchor && this._anchor.contains(e.target as Node)) {
      this._dismissedAnchor = this._anchor;
      this._dismissedAt = Date.now();
    }
    // Focus goes where they clicked, not back to the •••.
    this.lastFocusedElement = null;
    this._zone.run(() => this.choose(false));
  };

  private _onViewportChange = () => {
    if (!this._anchor?.isConnected) {
      this._zone.run(() => this.choose(false));
      return;
    }
    this._positionMenu();
    this._cdr.detectChanges();
  };

  private _listening = false;

  private _attachMenuListeners() {
    if (this._listening) return;
    this._listening = true;
    this._zone.runOutsideAngular(() => {
      document.addEventListener("pointerdown", this._onDocPointerDown, true);
      // Capture, so scrolling ANY container (the feed, the video's comment list) moves it with the •••.
      window.addEventListener("scroll", this._onViewportChange, true);
      window.addEventListener("resize", this._onViewportChange);
    });
  }

  private _detachMenuListeners() {
    if (!this._listening) return;
    this._listening = false;
    document.removeEventListener("pointerdown", this._onDocPointerDown, true);
    window.removeEventListener("scroll", this._onViewportChange, true);
    window.removeEventListener("resize", this._onViewportChange);
  }
}
